import Chart from 'chart.js/auto';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import {unByKey} from 'ol/Observable';
import DragPan from 'ol/interaction/DragPan';
import {toLonLat} from 'ol/proj';
 import {show_selection} from './main.js';

const GRAPH_TYPES = [
  {value: 'histogram', label: 'Histogram of CO2 values'},
  {value: 'date-scatter', label: 'Scatterplot of CO2 values by date'},
  {value: 'latitude-scatter', label: 'CO2 Values by Latitude'},
  {value: 'chl-scatter', label: 'Scatterplot of CO2 values vs Chl'},
  {value: 'chl-over-time', label: 'Chlorophyll average by date'},
  {value: 'co2-tile-3d', label: 'CO2 Tile Graph (3D)'},
  {value: 'estimated-co2-removal', label: 'Estimated CO2 removal'},
  {value: 'gradient-chl-scatter', label: 'Scatterplot of Gradient vs Chl'},
  {value: 'gradient-vorticity-scatter', label: 'Scatterplot of Gradient vs Vorticity'},
  {value: 'gradient-chl-vorticity-scatter', label: 'Scatterplot of Gradient vs Chl x Vorticity'}
];

const RAW_CO2_SHOW_SELECTIONS = new Set(['raw', 'raw_avg12', 'raw_avg25']);

// EPSG:3857 (Web Mercator) world width in meters. With wrapX enabled, the map can render/interact with any
// repeated copy of the world, so feature and rectangle coordinates may differ by a multiple of this value
// even when they visually overlap.
const WORLD_WIDTH_METERS = 2 * Math.PI * 6378137;

// Gradient Show selections eligible for the gradient-vs-Chl graph, mapped to their row field and axis label.
const GRADIENT_SHOW_SELECTIONS = {
  delta: {field: 'co2_derivative', label: 'Gradient (ppm/hr)'},
  delta_avg12: {field: 'gradient_avg_12', label: 'Gradient Avg 12km (ppm/hr)'},
  delta_avg25: {field: 'gradient_avg_25', label: 'Gradient Avg 25km (ppm/hr)'}
};

const finite = (value) => Number.isFinite(Number.parseFloat(value))
  ? Number.parseFloat(value)
  : null;

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Invalid date' : date.toISOString().slice(0, 10);
};

// Vorticity (and Chl x Vorticity) values are tiny (~1e-6), so the default tooltip rounding shows them as 0.
const formatPreciseNumber = (value) => {
  if (!Number.isFinite(value)) return value;
  if (value !== 0 && Math.abs(value) < 0.001) return value.toExponential(3);
  return value.toLocaleString(undefined, {maximumFractionDigits: 6});
};

const degreesToRadians = (value) => value * Math.PI / 180;

const distanceBetweenLonLatKm = (firstPoint, secondPoint) => {
  const earthRadiusKm = 6371;
  const firstLat = degreesToRadians(firstPoint.lat);
  const secondLat = degreesToRadians(secondPoint.lat);
  const deltaLat = degreesToRadians(secondPoint.lat - firstPoint.lat);
  const deltaLon = degreesToRadians(secondPoint.lon - firstPoint.lon);
  const haversine = Math.sin(deltaLat / 2) ** 2
    + Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(deltaLon / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(haversine));
};

const bearingBetweenLonLat = (firstPoint, secondPoint) => {
  const firstLat = degreesToRadians(firstPoint.lat);
  const secondLat = degreesToRadians(secondPoint.lat);
  const deltaLon = degreesToRadians(secondPoint.lon - firstPoint.lon);
  return Math.atan2(
    Math.sin(deltaLon) * Math.cos(secondLat),
    Math.cos(firstLat) * Math.sin(secondLat) - Math.sin(firstLat) * Math.cos(secondLat) * Math.cos(deltaLon)
  );
};

const acuteAngleBetween = (firstBearing, secondBearing) => {
  let angle = Math.abs(firstBearing - secondBearing) % Math.PI;
  if (angle > Math.PI / 2) angle = Math.PI - angle;
  return angle;
};

const meanLineLabelPlugin = {
  id: 'mean-xco2-line-label',
  afterDatasetsDraw(chart) {
    const meanDataset = chart.data.datasets.find((dataset) => dataset.isMeanXco2Line);
    if (!meanDataset || !meanDataset.data.length) return;

    const yValue = meanDataset.data[0].y;
    const yPixel = chart.scales.y.getPixelForValue(yValue);
    const {ctx, chartArea} = chart;
    ctx.save();
    ctx.fillStyle = '#333';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    const padding = 4;
    const labelX = chartArea.right;
    const labelY = yPixel + 4;
    const labelWidth = ctx.measureText(meanDataset.label).width;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.fillRect(labelX - labelWidth - padding, labelY - padding, labelWidth + padding * 2, 12 + padding * 2);
    ctx.fillStyle = '#333';
    ctx.fillText(meanDataset.label, labelX, labelY);
    ctx.restore();
  }
};

export class GraphTool {
  constructor({map, getDisplayedFeatures, getChlValue, chlEnabled, getIffyPointSymbol, getValleyBounds, valleysEnabled, getGraphSummary, getGradientChlPoints, getGradientVorticityPoints, getGradientChlVorticityPoints, getFullDateRange, getChlOverTimePoints, button}) {
    this.map = map;
    this.getDisplayedFeatures = getDisplayedFeatures;
    this.getChlValue = getChlValue;
    this.chlEnabled = chlEnabled;
    this.getIffyPointSymbol = getIffyPointSymbol;
    this.getValleyBounds = getValleyBounds;
    this.valleysEnabled = valleysEnabled;
    this.getGraphSummary = getGraphSummary;
    this.getGradientChlPoints = getGradientChlPoints;
    this.getGradientVorticityPoints = getGradientVorticityPoints;
    this.getGradientChlVorticityPoints = getGradientChlVorticityPoints;
    this.getFullDateRange = getFullDateRange;
    this.getChlOverTimePoints = getChlOverTimePoints;
    this.button = button;
    this.hintElement = null;
    this.hintTimeoutId = null;
    this.rectangleSource = new VectorSource();
    this.rectangleLayer = new VectorLayer({source: this.rectangleSource, zIndex: 400});
    this.map.addLayer(this.rectangleLayer);
    this.overlay = null;
    this.chart = null;
    this.active = false;
    this.dragging = false;
    this.startCoordinate = null;
    this.moveKey = null;
    this.upKey = null;
    this.downKey = null;
    this.valleyPickKey = null;
    this.valleyPickActive = false;
    this.selectedValleyRow = null;
    this.estimatedRows = null;
    this.estimatedMeanInput = null;
    this.blockNextClick = false;
    this.dragPan = this.map.getInteractions().getArray().find((interaction) => interaction instanceof DragPan);
    this.dragPanWasActive = false;
    this.button.addEventListener('click', () => {
      if (this.active) {
        this.cancel();
      } else {
        this.activate();
      }
    });
    this.escapeKeyHandler = (event) => {
      if (event.key === 'Escape' && this.valleyPickActive) {
        event.preventDefault();
        this.cancelValleyPick();
      } else if (event.key === 'Escape' && this.active) {
        event.preventDefault();
        this.cancel();
      }
    };
    document.addEventListener('keydown', this.escapeKeyHandler);
  }

  shouldBlockMapInteractions() {
    return this.active || this.valleyPickActive;
  }

  consumeMapClick() {
    if (!this.blockNextClick) return false;
    this.blockNextClick = false;
    return true;
  }

  activate() {
    if (this.active) return;
    this.active = true;
    this.button.classList.add('active');
    this.button.textContent = 'Cancel Graph';
    this.map.getTargetElement().style.cursor = 'crosshair';
    this.showHintMessage('Drag a rectangle to select points to be graphed, then select the graph type. If you want CO2 Removal estimate, select a large rectangle to establish an ambient CO2 level, then set the graph type to Estimated CO2 Removal');
    if (this.dragPan) {
      this.dragPanWasActive = this.dragPan.getActive();
      this.dragPan.setActive(false);
    }
    this.downKey = this.map.on('pointerdown', (event) => this.handlePointerDown(event));
    this.moveKey = this.map.on('pointermove', (event) => this.handlePointerMove(event));
    this.upKey = this.map.on('pointerup', (event) => this.handlePointerUp(event));
  }

  deactivate() {
    this.active = false;
    this.dragging = false;
    this.startCoordinate = null;
    if (this.downKey) unByKey(this.downKey);
    if (this.moveKey) unByKey(this.moveKey);
    if (this.upKey) unByKey(this.upKey);
    this.downKey = null;
    this.moveKey = null;
    this.upKey = null;
    if (this.dragPan) {
      this.dragPan.setActive(this.dragPanWasActive);
    }
    this.button.classList.remove('active');
    this.button.textContent = 'Graph';
    this.map.getTargetElement().style.cursor = '';
  }

  cancel() {
    if (!this.active) return;
    this.deactivate();
    this.rectangleSource.clear();
  }

  // Briefly show an instructional message over the map, replacing any hint already showing.
  showHintMessage(text, durationMs = 10000) {
    const target = this.map.getTargetElement();
    if (!target) return;
    this.clearHintMessage();
    const hint = document.createElement('div');
    hint.className = 'graph-hint-message';
    hint.textContent = text;
    target.appendChild(hint);
    this.hintElement = hint;
    this.hintTimeoutId = setTimeout(() => this.clearHintMessage(), durationMs);
  }

  clearHintMessage() {
    if (this.hintTimeoutId) {
      clearTimeout(this.hintTimeoutId);
      this.hintTimeoutId = null;
    }
    if (this.hintElement) {
      this.hintElement.remove();
      this.hintElement = null;
    }
  }

  handlePointerDown(event) {
    if (!this.active || event.originalEvent.button !== 0) return;
    this.dragging = true;
    this.startCoordinate = event.coordinate;
    this.rectangleSource.clear();
  }

  handlePointerMove(event) {
    if (!this.active || !this.dragging || !this.startCoordinate) return;
    this.drawRectangle(this.startCoordinate, event.coordinate);
  }

  handlePointerUp(event) {
    if (!this.active || !this.dragging || !this.startCoordinate) return;
    const start = this.startCoordinate;
    const end = event.coordinate;
    this.dragging = false;
    this.drawRectangle(start, end);
    this.blockNextClick = true;
    this.deactivate();
    this.showGraph(start, end);
  }

  drawRectangle(start, end) {
    const minX = Math.min(start[0], end[0]);
    const maxX = Math.max(start[0], end[0]);
    const minY = Math.min(start[1], end[1]);
    const maxY = Math.max(start[1], end[1]);
    const polygon = new Polygon([[
      [minX, minY], [minX, maxY], [maxX, maxY], [maxX, minY], [minX, minY]
    ]]);
    const feature = new Feature({geometry: polygon, isGraphRectangle: true});
    feature.setStyle(new Style({
      fill: new Fill({color: 'rgba(30, 110, 210, 0.12)'}),
      stroke: new Stroke({color: '#1e6ed2', width: 2, lineDash: [8, 5]})
    }));
    this.rectangleSource.clear();
    this.rectangleSource.addFeature(feature);
  }

  getSelectedRows(start, end) {
    const minX = Math.min(start[0], end[0]);
    const maxX = Math.max(start[0], end[0]);
    const minY = Math.min(start[1], end[1]);
    const maxY = Math.max(start[1], end[1]);
    const rectCenterX = (minX + maxX) / 2;
    return this.getDisplayedFeatures().map((feature) => {
      const geometry = feature.getGeometry();
      const coordinate = geometry?.getCoordinates?.();
      if (!coordinate || coordinate.length < 2 || !Number.isFinite(coordinate[0]) || !Number.isFinite(coordinate[1])) {
        return null;
      }
      // Shift the point into the same world copy as the rectangle so panning across the antimeridian
      // (e.g. after switching locations) doesn't make an otherwise-enclosed point fail the bounds check.
      const worldsAway = Math.round((coordinate[0] - rectCenterX) / WORLD_WIDTH_METERS);
      const x = coordinate[0] - worldsAway * WORLD_WIDTH_METERS;
      if (x < minX || x > maxX || coordinate[1] < minY || coordinate[1] > maxY) {
        return null;
      }
      return feature.getProperties();
    }).filter(Boolean);
  }

  showGraph(start, end) {
    const rows = this.getSelectedRows(start, end);
    this.createOverlay(rows, start, end);
  }

  createOverlay(rows, start, end) {
    this.closeOverlay(false);
    const target = this.map.getTargetElement();
    const overlay = document.createElement('div');
    overlay.className = 'graph-overlay';
    overlay.innerHTML = `
      <div class="graph-overlay-header">
        <strong id="graph-title">Data Graph</strong>
        <button type="button" class="graph-overlay-close" title="Close graph">&times;</button>
      </div>
      <div class="graph-canvas-wrap"><canvas></canvas><div class="graph-plotly" style="display:none"></div></div>
        <span class="graph-skipped-count"></span>
        <div class="graph-controls">
        <label>Graph:
          <select class="graph-type-select">
            <option value="">Select a graph type...</option>
            ${GRAPH_TYPES.map((type) => `<option value="${type.value}">${type.label}</option>`).join('')}
          </select>
        </label>
        <span class="graph-point-count"></span>
        <span class="latitude-source-controls" hidden>
          <label><input class="color-by-source" type="checkbox"> Color by source</label>
          <span class="latitude-source-key" aria-label="Source colors" hidden>
            <span><i class="source-swatch source-oco2"></i>OCO2</span>
            <span><i class="source-swatch source-oco3"></i>OCO3</span>
            <span><i class="source-swatch source-socat"></i>SOCAT</span>
          </span>
        </span>
        <span class="tile-size-controls" hidden>
          <span>Tile size: <span class="tile-size-value">5</span>&deg;</span>
          <input class="tile-size-slider" type="range" min=".5" max="5" step=".5" value="5">
        </span>

        <span class="estimated-co2-controls"></span>
      </div>
      <div class="graph-summary-footer"></div>`;
    target.appendChild(overlay);
    this.overlay = overlay;
    const summaryFooter = overlay.querySelector('.graph-summary-footer');
    if (summaryFooter) summaryFooter.textContent = this.getGraphSummary?.() || '';
    overlay.style.left = `${Math.max(12, target.clientWidth / 2 - 290)}px`;
    overlay.style.top = `${Math.max(12, target.clientHeight / 2 - 210)}px`;
    overlay.querySelector('.graph-overlay-close').addEventListener('click', () => this.closeOverlay(true));
    const typeSelect = overlay.querySelector('.graph-type-select');
    const latitudeSourceControls = overlay.querySelector('.latitude-source-controls');
    const updateLatitudeSourceControls = (type) => {
      latitudeSourceControls.hidden = type !== 'latitude-scatter';
    };
    const colorBySourceCheckbox = overlay.querySelector('.color-by-source');
    const latitudeSourceKey = overlay.querySelector('.latitude-source-key');
    colorBySourceCheckbox.addEventListener('change', () => {
      latitudeSourceKey.hidden = !colorBySourceCheckbox.checked;
      if (typeSelect.value === 'latitude-scatter') this.renderChart('latitude-scatter', rows);
    });
    const tileControls = overlay.querySelector('.tile-size-controls');
    const tileSlider = overlay.querySelector('.tile-size-slider');
    const tileValue = overlay.querySelector('.tile-size-value');
    tileSlider.addEventListener('input', () => {
      tileValue.textContent = tileSlider.value;
      if (typeSelect.value === 'co2-tile-3d') this.renderTileGraph(rows, Number(tileSlider.value));
    });
    typeSelect.addEventListener('change', (event) => {
      if (!event.target.value) {
        updateLatitudeSourceControls('');
        tileControls.hidden = true;
        return;
      }
      updateLatitudeSourceControls(event.target.value);
      tileControls.hidden = event.target.value !== 'co2-tile-3d';
      this.setPlotlyMode(event.target.value === 'co2-tile-3d');
      this.cancelValleyPick();
      this.clearEstimatedControls();
      if (event.target.value === 'co2-tile-3d') {
        overlay.querySelector('.graph-skipped-count').textContent = '';
        this.renderTileGraph(rows, Number(tileSlider.value));
        return;
      }
      if (event.target.value === 'estimated-co2-removal') {
        this.prepareEstimatedRemovalGraph(rows);
        return;
      }
      if (event.target.value === 'chl-scatter' && !this.isChlAvailable()) {
        window.alert('This graph requires Chl to be checked.');
        event.target.value = '';
        return;
      }
      if (event.target.value === 'chl-over-time') {
        overlay.querySelector('.graph-skipped-count').textContent = '';
        this.renderChlOverTimeChart();
        return;
      }
      const requiresGradientShow = event.target.value === 'gradient-chl-scatter' ||
        event.target.value === 'gradient-vorticity-scatter' ||
        event.target.value === 'gradient-chl-vorticity-scatter';
      if (requiresGradientShow && !GRADIENT_SHOW_SELECTIONS[show_selection]) {
        window.alert('This graph requires Show to be set to Gradient, Gradient Avg 12km, or Gradient Avg 25km.');
        event.target.value = '';
        return;
      }
      if (event.target.value === 'gradient-chl-scatter') {
        this.renderGradientChlChart(rows);
        return;
      }
      if (event.target.value === 'gradient-vorticity-scatter') {
        this.renderGradientVorticityChart(rows);
        return;
      }
      if (event.target.value === 'gradient-chl-vorticity-scatter') {
        this.renderGradientChlVorticityChart(rows);
        return;
      }
      overlay.querySelector('.graph-skipped-count').textContent = '';
      this.renderChart(event.target.value, rows);
    });
    this.enableDragging(overlay.querySelector('.graph-overlay-header'), overlay);
    overlay.querySelector('.graph-point-count').textContent = `${rows.length} displayed points in box`;
    this.rectangleExtent = {start, end};
    // For plain CO2 value views (Raw, Avg 12km, Avg 25km), default straight to the latitude graph.
    if (RAW_CO2_SHOW_SELECTIONS.has(show_selection)) {
      typeSelect.value = 'latitude-scatter';
      updateLatitudeSourceControls('latitude-scatter');
      this.renderChart('latitude-scatter', rows);
    }
  }

  isChlAvailable() {
    return this.chlEnabled?.() === true;
  }

  clearEstimatedControls() {
    this.cancelValleyPick();
    this.selectedValleyRow = null;
    this.estimatedRows = null;
    this.estimatedMeanInput = null;
    const controls = this.overlay?.querySelector('.estimated-co2-controls');
    if (controls) controls.innerHTML = '';
  }

  prepareEstimatedRemovalGraph(rows) {
    this.overlay.querySelector('#graph-title').textContent = `Estimated CO2Removal Graph for <select a valley>`;
    if (!['raw_avg12', 'raw_avg25'].includes(show_selection)) {
      window.alert('This graph requires Show to be set to Avg 12km or Avg 25km.');
      this.overlay.querySelector('.graph-type-select').value = '';
      return;
    }
    if (!this.valleysEnabled?.()) {
      window.alert('This graph requires the Valleys checkbox to be checked.');
      this.overlay.querySelector('.graph-type-select').value = '';
      return;
    }

    const xco2Field = show_selection === 'raw_avg25' ? 'xco2_avg_25' : 'xco2_avg_12';
    const meanValues = rows
      .filter((row) => !this.getIffyPointSymbol?.(row))
      .map((row) => finite(row[xco2Field]))
      .filter((value) => value !== null);
    if (!meanValues.length) {
      window.alert('No usable XCO2 values were selected in the rectangle.');
      this.overlay.querySelector('.graph-type-select').value = '';
      return;
    }

    const controls = this.overlay.querySelector('.estimated-co2-controls');
    controls.innerHTML = `Ambient XCO2 value: <input class="estimated-co2-mean" type="number" step="0.01"> <button type="button" class="pick-valley-button">Pick Valley</button>`;
    this.overlay.querySelector('.graph-point-count').textContent = "";
    this.estimatedRows = rows;
    this.estimatedMeanInput = controls.querySelector('.estimated-co2-mean');
    this.estimatedMeanInput.value = (meanValues.reduce((sum, value) => sum + value, 0) / meanValues.length).toFixed(2);
    this.estimatedMeanInput.addEventListener('change', () => {
      if (!this.selectedValleyRow) return;
      const centerValue = finite(this.estimatedMeanInput.value);
      if (centerValue === null) {
        window.alert('Mean XCO2 value must be a number.');
        this.estimatedMeanInput.value = this.estimatedMeanInput.dataset.lastValidValue;
        return;
      }
      this.estimatedMeanInput.dataset.lastValidValue = centerValue;
      this.renderEstimatedRemovalChart(this.estimatedRows, centerValue, this.selectedValleyRow);
    });
    this.estimatedMeanInput.dataset.lastValidValue = this.estimatedMeanInput.value;
    controls.querySelector('.pick-valley-button').addEventListener('click', () => this.startValleyPick());
    this.startValleyPick();
  }

  startValleyPick() {
    if (!this.overlay || this.valleyPickActive) return;
    this.valleyPickActive = true;
    this.map.getTargetElement().style.cursor = 'crosshair';
    this.showHintMessage('Click a highlighted valley point to calculate estimated CO2 removal');
    this.valleyPickKey = this.map.on('click', (event) => {
      let valleyFeature = null;
      this.map.forEachFeatureAtPixel(this.map.getEventPixel(event.originalEvent), (feature) => {
        if (valleyFeature || (!feature.get('isGraphDataPoint') && !feature.get('isExtremaIcon'))) return;
        if (this.getValleyBounds?.(feature.getProperties())) valleyFeature = feature;
      });
      if (!valleyFeature) return;
      this.selectedValleyRow = valleyFeature.getProperties();
      this.cancelValleyPick();
      const centerValue = finite(this.estimatedMeanInput?.value);
      if (centerValue !== null) {
        this.renderEstimatedRemovalChart(this.estimatedRows, centerValue, this.selectedValleyRow);
      }
    });
  }

  cancelValleyPick() {
    if (this.valleyPickKey) unByKey(this.valleyPickKey);
    this.valleyPickKey = null;
    this.valleyPickActive = false;
    this.clearHintMessage();
    if (this.map.getTargetElement()) this.map.getTargetElement().style.cursor = this.active ? 'crosshair' : '';
  }

  renderEstimatedRemovalChart(rows, centerValue, valleyRow) {
    // make a string with date (from first 8 digits of sounding_id), latitude, and longitude for the graph title
    const graphTitle = `Estimated CO2 Removal Graph for ${valleyRow.sounding_id.slice(0, 8)} at (${parseFloat(valleyRow.latitude).toFixed(2)}, ${parseFloat(valleyRow.longitude).toFixed(2)})`;
    this.overlay.querySelector('#graph-title').textContent = graphTitle;


    // Check if the valley row is valid and get set of data points to average
    const valleyBounds = this.getValleyBounds?.(valleyRow);
    if (!valleyBounds) return;
    const xco2Field = show_selection === 'raw_avg25' ? 'xco2_avg_25' : 'xco2_avg_12';
    const valleyPoints = this.getDisplayedFeatures()
      .map((feature) => feature.getProperties())
      .filter((row) => row._index >= valleyBounds.leftIndex && row._index <= valleyBounds.rightIndex)
      .filter((row) => !this.getIffyPointSymbol?.(row));
    const valleyValues = valleyPoints.map((row) => finite(row[xco2Field])).filter((value) => value !== null);
    if (!valleyValues.length) {
      window.alert('No usable XCO2 values were found in the selected valley.');
      return;
    }
    

    // Compute average CO2 level in valley
    const valleyAverage = valleyValues.reduce((sum, value) => sum + value, 0) / valleyValues.length;
    
    // Compute average wind speed components and convert to km/h
    const windValues = valleyPoints
      .map((row) => ({u: finite(row.u_wind), v: finite(row.v_wind)}))
      .filter((value) => value.u !== null && value.v !== null);
    const averageWindU_mps = windValues.length ? windValues.reduce((sum, value) => sum + value.u, 0) / windValues.length : null;
    const averageWindV_mps = windValues.length ? windValues.reduce((sum, value) => sum + value.v, 0) / windValues.length : null;
    const averageWindU = averageWindU_mps *3600/1000; // convert to km/h
    const averageWindV = averageWindV_mps *3600/1000;

    // Identify orbit start and end points for the selected valley to compute wind angle
    const orbitRows = this.getDisplayedFeatures()
      .map((feature) => feature.getProperties())
      .filter((row) => finite(row._index) !== null && finite(row.latitude) !== null && finite(row.longitude) !== null);
    const rowByIndex = new Map(orbitRows.map((row) => [finite(row._index), row]));
    const valleyIndex = finite(valleyRow._index);
    const fiveLeft = rowByIndex.get(valleyIndex - 5);
    const fiveRight = rowByIndex.get(valleyIndex + 5);
    let orbitStart = fiveLeft;
    let orbitEnd = fiveRight;
    if (!orbitStart || !orbitEnd) {
      const tenLeft = rowByIndex.get(valleyIndex - 10);
      const tenRight = rowByIndex.get(valleyIndex + 10);
      orbitStart = valleyRow;
      orbitEnd = tenLeft || tenRight;
    }
    if (!orbitStart || !orbitEnd) {
      window.alert('Unable to identify two orbit points for the selected valley.');
      return;
    }

    // Identify start and end points for an orbit line near the valley point
    const segment_start_point = {lat: finite(orbitStart.latitude), lon: finite(orbitStart.longitude)};
    const segment_end_point = {lat: finite(orbitEnd.latitude), lon: finite(orbitEnd.longitude)};


    const valleyWidthKm = distanceBetweenLonLatKm(
      {lat: finite(rowByIndex.get(valleyBounds.leftIndex)?.latitude), lon: finite(rowByIndex.get(valleyBounds.leftIndex)?.longitude)},
      {lat: finite(rowByIndex.get(valleyBounds.rightIndex)?.latitude), lon: finite(rowByIndex.get(valleyBounds.rightIndex)?.longitude)}
    );
    const valleyStart = rowByIndex.get(valleyBounds.leftIndex);
    const valleyEnd = rowByIndex.get(valleyBounds.rightIndex);
    if (!valleyStart || !valleyEnd || !Number.isFinite(valleyWidthKm)) {
      window.alert('Unable to identify both endpoints of the selected valley.');
      return;
    }
    const average_Wind_Speed = averageWindU === null || averageWindV === null
      ? null
      : Math.sqrt(averageWindU ** 2 + averageWindV ** 2);
    const wind_Orbit_Intersection_Angle = average_Wind_Speed === null
      ? null
      : acuteAngleBetween(Math.atan2(averageWindU, averageWindV), bearingBetweenLonLat(segment_start_point, segment_end_point));
    const adjusted_Wind_Speed = average_Wind_Speed === null || wind_Orbit_Intersection_Angle === null
      ? null
      : average_Wind_Speed * Math.sin(wind_Orbit_Intersection_Angle);
    const atmosphere_Height_Km = 8.5;
    const marine_Boundary_Layer_Height_Km = 0.85;
    const air_Volume_Per_Day = adjusted_Wind_Speed === null ? null : adjusted_Wind_Speed * marine_Boundary_Layer_Height_Km * valleyWidthKm * 24;
    const air_Mass_Per_Day = air_Volume_Per_Day === null ? null : air_Volume_Per_Day * (1.2 * 10 ** 6);

    console.log ("left index:", valleyBounds.leftIndex, "right index:", valleyBounds.rightIndex, "valley width km:", valleyWidthKm);
    console.log('average_Wind_Speed:', average_Wind_Speed);
    console.log('wind_Orbit_Intersection_Angle:', wind_Orbit_Intersection_Angle);
    console.log('adjusted_Wind_Speed:', adjusted_Wind_Speed);
    console.log('air_Volume_Per_Day:', air_Volume_Per_Day);
    console.log('air_Mass_Per_Day:', air_Mass_Per_Day);


    const xValues = Array.from({length: 7}, (_, index) => centerValue - .75 + index * 0.25);
    const points = xValues.map((ambientValue) => {
      const avg_Delta_Co2 = (ambientValue - valleyAverage) * 1.52;
      const avg_Delta_Co2_At_Surface = avg_Delta_Co2 * atmosphere_Height_Km / marine_Boundary_Layer_Height_Km;
      console.log('avg_Delta_Co2:', avg_Delta_Co2, 'avg_Delta_Co2_At_Surface:', avg_Delta_Co2_At_Surface, 
                  'removal:', air_Mass_Per_Day* avg_Delta_Co2_At_Surface * 1.0e-6);
      return air_Mass_Per_Day === null ? null : air_Mass_Per_Day * avg_Delta_Co2_At_Surface * 1.0e-6;  // ppm conversion
    });
    const canvas = this.overlay.querySelector('canvas');
    if (this.chart) this.chart.destroy();
    const options = this.baseOptions();
    options.scales.x = {title: {display: true, text: 'Ambient XCO2 (ppm)'}};
    options.scales.y = {
      title: {display: true, text: 'Removed CO2 (kilotons/day)'},
      ticks: {callback: (value) => (Number(value) / 1e3).toFixed(2)}
    };
    const barColors = xValues.map((value) => Math.abs(value - centerValue) < 1e-9 ? '#e09f3e' : '#4a8c65');
    this.chart = new Chart(canvas, {
      type: 'bar',
      data: {labels: xValues.map((value) => value.toFixed(2)), datasets: [{label: 'Estimated CO2 removal', data: points, backgroundColor: barColors}]},
      options
    });
    this.estimatedMeanInput.dataset.lastValidValue = centerValue;
    this.estimatedMeanInput.value = centerValue;
    this.overlay.querySelector('.graph-skipped-count').textContent = 
        `Valley XCO2 average: ${valleyAverage.toFixed(2)}; Valley width: ${valleyWidthKm.toFixed(2)} km; air vol per day: ${air_Volume_Per_Day === null ? 'N/A' : air_Volume_Per_Day.toPrecision(3)}; air mass per day: ${air_Mass_Per_Day === null ? 'N/A' : air_Mass_Per_Day.toPrecision(3)}`;
  }

  enableDragging(header, overlay) {
    header.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button')) return;
      event.preventDefault();
      const startX = event.clientX;
      const startY = event.clientY;
      const left = overlay.offsetLeft;
      const top = overlay.offsetTop;
      const move = (moveEvent) => {
        overlay.style.left = `${Math.max(0, left + moveEvent.clientX - startX)}px`;
        overlay.style.top = `${Math.max(0, top + moveEvent.clientY - startY)}px`;
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    });
  }

  renderChart(type, rows) {
    const canvas = this.overlay.querySelector('canvas');
    if (this.chart) this.chart.destroy();
    const datasets = [];
    let labels = [];
    let xTitle = '';
    let yTitle = '';
    let chartType = 'scatter';
    let options = this.baseOptions();

    if (type === 'histogram') {
      const values = rows.map((row) => finite(row.xco2)).filter((value) => value !== null);
      const bins = new Map();
      values.forEach((value) => {
        const bin = Math.floor(value * 4) / 4;
        bins.set(bin, (bins.get(bin) || 0) + 1);
      });
        labels = [...bins.keys()].sort((a, b) => a - b).map((value) => value.toFixed(2));
      chartType = 'bar';
      datasets.push({label: 'Points', data: labels.map((label) => bins.get(Number(label))), backgroundColor: 'rgba(30, 110, 210, 0.65)', borderColor: '#1e6ed2', borderWidth: 1});
      options.scales.x = {title: {display: true, text: 'XCO2 (ppm)'}};
      options.scales.y = {title: {display: true, text: 'Number of points'}, beginAtZero: true, ticks: {precision: 0}};
    } else if (type === 'date-scatter') {
      const points = rows.map((row) => {
        const xco2 = finite(row.xco2);
        const date = row._date || row.date || row.datetime || row.timestamp || row.sounding_id?.slice(0, 8);
        if (xco2 === null || !date) return null;
        const dateValue = row._date instanceof Date ? row._date.getTime() : new Date(date).getTime();
        return Number.isFinite(dateValue) ? {x: dateValue, y: xco2} : null;
      }).filter(Boolean);
      datasets.push({label: 'XCO2', data: points, backgroundColor: '#d14b31', pointRadius: 3});
      xTitle = 'Date';
      yTitle = 'XCO2 (ppm)';
      options.scales.x = {type: 'linear', title: {display: true, text: xTitle}, ticks: {callback: (value) => formatDate(value)}};
      options.scales.y = {title: {display: true, text: yTitle}};
    } else if (type === 'latitude-scatter') {
      const latitudeRows = rows.filter((row) => !this.getIffyPointSymbol?.(row));
      const pointRows = latitudeRows.map((row) => {
        const latitude = finite(row.latitude);
        const xco2 = finite(show_selection === 'raw_avg25' ? row.xco2_avg_25 :
                            (show_selection === 'raw_avg12' ? row.xco2_avg_12 :
                            row.xco2));
        return latitude !== null && xco2 !== null ? {
          point: {x: latitude, y: xco2},
          source: finite(row.satellite_id)
        } : null;
      }).filter(Boolean);
      const colorBySource = this.overlay.querySelector('.color-by-source')?.checked === true;
      const sourceConfig = [
        {id: 2, label: 'OCO2', color: '#799ae7'},
        {id: 3, label: 'OCO3', color: '#cddc77'},
        {id: 4, label: 'SOCAT', color: '#ed4962'}
      ];
      const points = pointRows
        .filter(({source}) => !colorBySource || sourceConfig.some((item) => item.id === source))
        .map(({point}) => point);
      if (colorBySource) {
        sourceConfig.forEach(({id, label, color}) => {
          datasets.push({
            label,
            data: pointRows.filter(({source}) => source === id).map(({point}) => point),
            backgroundColor: color,
            pointRadius: 3
          });
        });
      } else {
        datasets.push({label: 'XCO2 by Latitude', data: points, backgroundColor: '#b05a30', pointRadius: 3});
      }
      if (points.length > 0) {
        const mean = points.reduce((sum, point) => sum + point.y, 0) / points.length;
        const minLatitude = Math.min(...points.map((point) => point.x));
        const maxLatitude = Math.max(...points.map((point) => point.x));
        datasets.push({
          label: `Mean ${mean.toFixed(2)}`,
          data: [{x: minLatitude, y: mean}, {x: maxLatitude, y: mean}],
          isMeanXco2Line: true,
          showLine: true,
          pointRadius: 0,
          borderColor: '#222',
          borderWidth: 1.5,
          borderDash: [6, 4]
        });
      }
      options.scales.x = {title: {display: true, text: 'Latitude (degrees)'}};
      options.scales.y = {title: {display: true, text: (show_selection === 'raw_avg25' ?'XCO2 (avg 25km) (ppm)' :
                                                        (show_selection === 'raw_avg12' ? 'XCO2 (avg 12km) (ppm)' :
                                                        'XCO2 (ppm)'))}};
    } else {
      const points = rows.map((row) => {
        const chl = this.getChlValue(row.latitude, row.longitude);
        const xco2 = finite(row.xco2);
        return chl !== null && xco2 !== null ? {x: chl, y: xco2} : null;
      }).filter(Boolean);
      datasets.push({label: 'XCO2 vs Chl', data: points, backgroundColor: '#2a8c65', pointRadius: 3});
      options.scales.x = {title: {display: true, text: 'Chl (mg/m3)'}};
      options.scales.y = {title: {display: true, text: 'XCO2 (ppm)'}};
    }

    const plugins = type === 'latitude-scatter' ? [meanLineLabelPlugin] : [];
    this.chart = new Chart(canvas, {type: chartType, data: {labels, datasets}, options, plugins});
  }

  // Chlorophyll-over-time line graph: averages every 1d and 8d Chl grid cell inside the selected rectangle for
  // each available 1d/8d Chl file, and plots one point per file date against the dataset's full date range.
  async renderChlOverTimeChart() {
    if (!this.rectangleExtent || !this.getChlOverTimePoints) return;
    const skippedCountEl = this.overlay.querySelector('.graph-skipped-count');
    if (skippedCountEl) skippedCountEl.textContent = 'Loading Chl data...';

    const [startLon, startLat] = toLonLat(this.rectangleExtent.start);
    const [endLon, endLat] = toLonLat(this.rectangleExtent.end);
    const bounds = {
      minLat: Math.min(startLat, endLat),
      maxLat: Math.max(startLat, endLat),
      minLon: Math.min(startLon, endLon),
      maxLon: Math.max(startLon, endLon)
    };

    const {points1d, points8d} = await this.getChlOverTimePoints(bounds);
    if (!this.overlay) return; // Overlay may have been closed while awaiting Chl data.

    this.overlay.querySelector('#graph-title').textContent =
      `Chlorophyll average for (${bounds.minLat.toFixed(2)}, ${bounds.minLon.toFixed(2)}) to (${bounds.maxLat.toFixed(2)}, ${bounds.maxLon.toFixed(2)})`;
    const fullRange = this.getFullDateRange?.();
    const summaryFooter = this.overlay.querySelector('.graph-summary-footer');
    if (summaryFooter && fullRange) {
      summaryFooter.textContent = (this.getGraphSummary?.(false) || '') + ` ${fullRange.min.toISOString().slice(0, 10)} → ${fullRange.max.toISOString().slice(0, 10)}`;
    }
    this.overlay.querySelector('.graph-point-count').textContent = `${points1d.length + points8d.length} displayed points in box. Values > 10 excluded from avg.`;

    const canvas = this.overlay.querySelector('canvas');
    if (this.chart) this.chart.destroy();
    const options = this.baseOptions();
    options.plugins.legend = {display: true};
    // Show the point's date (rather than its raw millisecond x value) in the tooltip.
    options.plugins.tooltip = {
      callbacks: {
        title: (contexts) => contexts.map((context) => formatDate(context.parsed.x))
      }
    };
    options.scales.x = {
      type: 'linear',
      title: {display: true, text: 'Date'},
      ...(fullRange ? {min: fullRange.min.getTime(), max: fullRange.max.getTime()} : {}),
      ticks: {
        stepSize: 24 * 60 * 60 * 1000,
        callback: (value) => formatDate(value)
      }
    };
    options.scales.y = {title: {display: true, text: 'Chlorophyll (mg/m³)'}};
    this.chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: [],
        datasets: [{
          label: 'Chlorophyll 1d (mg/m³)',
          data: points1d,
          borderColor: '#2a8c65',
          backgroundColor: '#2a8c65',
          pointRadius: 2,
          showLine: true,
          spanGaps: true,
          borderWidth: 1, // make line thin
        }, {
          label: 'Chlorophyll 8d avg (mg/m³)',
          data: points8d,
          borderColor: '#31708f',
          backgroundColor: '#31708f',
          pointRadius: 3,
          showLine: true,
          spanGaps: true
        }]
      },
      options
    });

    if (skippedCountEl) {
      skippedCountEl.textContent = (points1d.length || points8d.length) ? '' : 'No Chl data found inside the selected box.';
    }
  }

  async renderGradientChlChart(rows) {
    const config = GRADIENT_SHOW_SELECTIONS[show_selection];
    if (!config || !this.getGradientChlPoints) return;
    const skippedCountEl = this.overlay.querySelector('.graph-skipped-count');
    if (skippedCountEl) skippedCountEl.textContent = 'Loading Chl data...';

    const {points, skippedCount} = await this.getGradientChlPoints(rows, config.field);
    if (!this.overlay) return; // Overlay may have been closed while awaiting Chl data.

    const canvas = this.overlay.querySelector('canvas');
    if (this.chart) this.chart.destroy();
    const options = this.baseOptions();
    options.scales.x = {title: {display: true, text: config.label}};
    options.scales.y = {title: {display: true, text: 'Chl (mg/m3)'}};
    this.chart = new Chart(canvas, {
      type: 'scatter',
      data: {labels: [], datasets: [{label: `${config.label} vs Chl`, data: points, backgroundColor: '#2a8c65', pointRadius: 3}]},
      options
    });

    if (skippedCountEl) {
      skippedCountEl.textContent = `${skippedCount} point${skippedCount === 1 ? '' : 's'} skipped (no Chl within 100km)`;
    }
  }

  // Gradient-vs-Vorticity graph: loads vorticity per point's own date (via main.js) and plots gradient (x) vs nearest vorticity (y).
  async renderGradientVorticityChart(rows) {
    const config = GRADIENT_SHOW_SELECTIONS[show_selection];
    if (!config || !this.getGradientVorticityPoints) return;
    const skippedCountEl = this.overlay.querySelector('.graph-skipped-count');
    if (skippedCountEl) skippedCountEl.textContent = 'Loading Vorticity data...';

    const {points, skippedCount} = await this.getGradientVorticityPoints(rows, config.field);
    if (!this.overlay) return; // Overlay may have been closed while awaiting Vorticity data.

    const canvas = this.overlay.querySelector('canvas');
    if (this.chart) this.chart.destroy();
    const options = this.baseOptions();
    options.scales.x = {title: {display: true, text: config.label}};
    options.scales.y = {title: {display: true, text: 'Vorticity (1/s)'}, ticks: {callback: (value) => formatPreciseNumber(value)}};
    // Vorticity values are tiny (~1e-6); format the tooltip explicitly so they don't round to 0.
    options.plugins.tooltip = {
      callbacks: {
        label: (context) => `(${formatPreciseNumber(context.parsed.x)}, ${formatPreciseNumber(context.parsed.y)})`
      }
    };
    this.chart = new Chart(canvas, {
      type: 'scatter',
      data: {labels: [], datasets: [{label: `${config.label} vs Vorticity`, data: points, backgroundColor: '#4a6fd1', pointRadius: 3}]},
      options
    });

    if (skippedCountEl) {
      skippedCountEl.textContent = `${skippedCount} point${skippedCount === 1 ? '' : 's'} skipped (no Vorticity within 50km)`;
    }
  }

  // Gradient-vs-(Chl x Vorticity) graph: loads Chl and Vorticity per point's own date (via main.js), multiplies them, and plots gradient (x) vs the product (y).
  async renderGradientChlVorticityChart(rows) {
    const config = GRADIENT_SHOW_SELECTIONS[show_selection];
    if (!config || !this.getGradientChlVorticityPoints) return;
    const skippedCountEl = this.overlay.querySelector('.graph-skipped-count');
    if (skippedCountEl) skippedCountEl.textContent = 'Loading Chl and Vorticity data...';

    const {points, skippedCount} = await this.getGradientChlVorticityPoints(rows, config.field);
    if (!this.overlay) return; // Overlay may have been closed while awaiting Chl/Vorticity data.

    const canvas = this.overlay.querySelector('canvas');
    if (this.chart) this.chart.destroy();
    const options = this.baseOptions();
    options.scales.x = {title: {display: true, text: config.label}};
    options.scales.y = {title: {display: true, text: 'Chl x Vorticity'}, ticks: {callback: (value) => formatPreciseNumber(value)}};
    // Chl x Vorticity values are tiny (~1e-6); format the tooltip explicitly so they don't round to 0.
    options.plugins.tooltip = {
      callbacks: {
        label: (context) => `(${formatPreciseNumber(context.parsed.x)}, ${formatPreciseNumber(context.parsed.y)})`
      }
    };
    this.chart = new Chart(canvas, {
      type: 'scatter',
      data: {labels: [], datasets: [{label: `${config.label} vs Chl x Vorticity`, data: points, backgroundColor: '#9b59b6', pointRadius: 3}]},
      options
    });

    if (skippedCountEl) {
      skippedCountEl.textContent = `${skippedCount} point${skippedCount === 1 ? '' : 's'} skipped (no Chl or Vorticity within 50km)`;
    }
  }

  baseOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {legend: {display: false}},
      scales: {
        x: {title: {display: true, text: ''}},
        y: {title: {display: true, text: ''}}
      }
    };
  }

  // Switch the chart area between the Chart.js canvas and the Plotly 3D container.
  setPlotlyMode(enabled) {
    const canvas = this.overlay?.querySelector('canvas');
    const plotDiv = this.overlay?.querySelector('.graph-plotly');
    if (!canvas || !plotDiv) return;
    if (enabled && this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    if (!enabled && this.plotly) this.plotly.purge(plotDiv);
    canvas.style.display = enabled ? 'none' : 'block';
    plotDiv.style.display = enabled ? 'block' : 'none';
  }

  // 3D surface of the mean XCO2 per lat/lon tile inside the selected rectangle.
  async renderTileGraph(rows, tileSize) {
    if (!this.rectangleExtent) return;
    const [startLon, startLat] = toLonLat(this.rectangleExtent.start);
    const [endLon, endLat] = toLonLat(this.rectangleExtent.end);
    // Undo map world-wrapping so the box matches the -180..180 longitudes in the data.
    const lonShift = Math.round((startLon + endLon) / 2 / 360) * 360;
    const minLat = Math.min(startLat, endLat);
    const maxLat = Math.max(startLat, endLat);
    const minLon = Math.min(startLon, endLon) - lonShift;
    const maxLon = Math.max(startLon, endLon) - lonShift;
    const nLat = Math.max(1, Math.ceil((maxLat - minLat) / tileSize));
    const nLon = Math.max(1, Math.ceil((maxLon - minLon) / tileSize));

    const sums = Array.from({length: nLat}, () => new Array(nLon).fill(0));
    const counts = Array.from({length: nLat}, () => new Array(nLon).fill(0));
    // Same mean as the CO2 Values by Latitude graph: all non-iffy points in the selection, not per tile.
    let overallSum = 0;
    let overallCount = 0;
    rows.forEach((row) => {
      if (this.getIffyPointSymbol?.(row)) return;
      const latitude = finite(row.latitude);
      const longitude = finite(row.longitude);
      const xco2 = finite(show_selection === 'raw_avg25' ? row.xco2_avg_25 :
                          (show_selection === 'raw_avg12' ? row.xco2_avg_12 : row.xco2));
      if (latitude === null || longitude === null || xco2 === null) return;
      overallSum += xco2;
      overallCount += 1;
      if (latitude < minLat || latitude > maxLat || longitude < minLon || longitude > maxLon) return;
      const i = Math.min(nLat - 1, Math.floor((latitude - minLat) / tileSize));
      const j = Math.min(nLon - 1, Math.floor((longitude - minLon) / tileSize));
      sums[i][j] += xco2;
      counts[i][j] += 1;
    });

    // scatter3d takes parallel 1D arrays: one (lon, lat, mean) entry per tile that has data.
    const lons = [];
    const lats = [];
    const z_data = [];
    for (let i = 0; i < nLat; i += 1) {
      for (let j = 0; j < nLon; j += 1) {
        if (!counts[i][j]) continue;
        lats.push(minLat + (i + 0.5) * tileSize);
        lons.push(minLon + (j + 0.5) * tileSize);
        z_data.push(sums[i][j] / counts[i][j]);
      }
    }
    const tilesWithData = z_data.length;

    if (!this.plotly) this.plotly = (await import('plotly.js-dist-min')).default;
    const plotDiv = this.overlay?.querySelector('.graph-plotly');
    if (!plotDiv || this.overlay.querySelector('.graph-type-select').value !== 'co2-tile-3d') return;

    this.overlay.querySelector('#graph-title').textContent = `CO2 Tile Graph (3D) - ${tileSize}\u00b0 tiles`;
    const trace = {
      type: 'scatter3d', //'surface',
      y: lons,
      x: lats,
      z: z_data,
      mode: 'markers',
      marker: {
        size: 6,
        line: {
        color: 'rgba(151, 49, 49, 0.14)',
        width: 0.5},
        opacity: 0.8,
        color: z_data,
        colorscale: 'Viridis',
        colorbar: {title: {text: 'ppm', side: 'right'}, thickness: 10, len: 0.8}
      }
    };
    const layout = {
      margin: {l: 0, r: 0, t: 0, b: 0},
      uirevision: 'tile-graph',
      scene: {
        yaxis: {title: {text: 'Longitude'}},
        xaxis: {title: {text: 'Latitude'}, autorange: 'reversed'},
        zaxis: {title: {text: 'XCO2 (ppm)'}}
      }
    };
    const meanLevel = overallCount ? overallSum / overallCount : null;
    const meanPlane = meanLevel === null ? null : {
      type: 'surface',
      y: [minLon, maxLon],
      x: [minLat, maxLat],
      z: [[meanLevel, meanLevel], [meanLevel, meanLevel]],
      name: `Mean ${meanLevel.toFixed(2)} ppm`,
      showscale: false,
      showlegend: false,
      opacity: 0.5,
      colorscale: [[0, '#d3d3d3'], [1, '#d3d3d3']],
      hovertemplate: `Mean ${meanLevel.toFixed(2)} ppm<extra></extra>`
    };
    await this.plotly.react(plotDiv, meanPlane ? [trace, meanPlane] : [trace], layout, {responsive: true, displaylogo: false});
    const skippedCountEl = this.overlay?.querySelector('.graph-skipped-count');
    if (skippedCountEl && meanLevel !== null) {
      // Each tile's area is its true ground area in km2 (shrinks with cos(latitude)).
      const kmPerDegree = 111.195;
      const stats = {above: 0, below: 0, aboveArea: 0, belowArea: 0, addition: 0, removal: 0};
      z_data.forEach((tileMean, k) => {
        const delta = tileMean - meanLevel;
        const area = (tileSize * kmPerDegree) ** 2 * Math.cos(degreesToRadians(lats[k]));
        if (delta > 0) {
          stats.above += 1;
          stats.aboveArea += area;
          stats.addition += area * delta;
        } else if (delta < 0) {
          stats.below += 1;
          stats.belowArea += area;
          stats.removal += area * delta;
        }
      });
      const km2 = 'km<sup>2</sup>';

      // Function to take  number and return it in scientific notation if > 1000 (E3) or > 1000000 (E6)
      // Handle positive or negative numbers for scientific notation
      const formatNumber = (num) => {
        if (num > 1e6) return (num / 1e6).toFixed(1) + 'E6';
        if (num > 1e3) return (num / 1e3).toFixed(1) + 'E3';
        if (num < -1e6) return (num / 1e6).toFixed(1) + 'E6';
        if (num < -1e3) return (num / 1e3).toFixed(1) + 'E3';
        return num.toFixed(1);
      };

      skippedCountEl.innerHTML = `${nLat * nLon} tiles: ${stats.above} (${formatNumber(stats.aboveArea)} ${km2}) above, `
        + `${stats.below} (${formatNumber(stats.belowArea)} ${km2}) below, `
        + `${nLat * nLon - tilesWithData} no data. `
        + `ppm ${km2}: +${formatNumber(stats.addition)} add, ${formatNumber(stats.removal)} remove`
        ;
    } else if (skippedCountEl) {
      skippedCountEl.textContent = `${nLat * nLon} tiles, no data`;
    }
  }

  closeOverlay(removeRectangle = true) {
    this.cancelValleyPick();
    this.clearEstimatedControls();
    const plotDiv = this.overlay?.querySelector('.graph-plotly');
    if (plotDiv && this.plotly) this.plotly.purge(plotDiv);
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    if (removeRectangle) this.rectangleSource.clear();
    this.rectangleExtent = null;
  }

  destroy() {
    this.deactivate();
    this.cancelValleyPick();
    this.closeOverlay(true);
    this.clearHintMessage();
    this.map.removeLayer(this.rectangleLayer);
    document.removeEventListener('keydown', this.escapeKeyHandler);
  }
}
